// Route table + auth gate. The account-gated routes are J9's real screens
// (SCREEN_ROUTES); they assume a surrounding <ScreensProvider>, which ReadyApp
// mounts for the authenticated tree. Onboarding needs no provider, so <App/> is
// also renderable on its own (pre-auth) — see main.tsx / AppRoot.
//
// Desktop (≥768px) renders EVERY authed route inside the two-pane AppShell:
// sidebar (the whole nav) + pane (route content), with the incoming-call modal
// overlaying the shell (ReadyApp's /incoming navigation is compact-only).
// `/` and `/contacts` collapse into the sidebar on desktop, so their pane shows
// the EmptyPane default. Compact keeps the tab/bare split exactly as before.

import { useEffect, useRef, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { OnboardingFlow } from '../onboarding/index.js';
import { IncomingCallScreen, SCREEN_ROUTES, useScreens } from '../screens/index.js';
import { AppShell, useIsDesktop } from '../screens/ui/AppShell.js';
import { EmptyPane } from '../screens/ui/EmptyPane.js';
import { Sidebar } from '../screens/ui/Sidebar.js';
import { useStore } from '../screens/ui/useStore.js';
import { useT } from '../i18n/index.js';
import { useAccount } from './accountContext.js';

function LoadingScreen(): ReactNode {
  const t = useT();
  return (
    <main className="flex min-h-full items-center justify-center">
      <p className="text-muted" role="status">
        {t('common.loading')}
      </p>
    </main>
  );
}

// Shown when the server definitively revoked this device's session (remote
// terminate / forced re-login) and local state was wiped: an explicit
// explanation + a path back in, instead of a browsable shell with dead APIs.
function SessionEndedScreen(): ReactNode {
  const t = useT();
  const navigate = useNavigate();
  return (
    <main className="mx-auto flex min-h-full w-full max-w-app flex-col items-center justify-center gap-6 p-6 text-center">
      <h1 className="text-2xl font-semibold text-ink">{t('auth.sessionEnded.title')}</h1>
      <p className="text-muted" role="status">
        {t('auth.sessionEnded.body')}
      </p>
      <button
        type="button"
        onClick={() => void navigate('/onboarding')}
        className="min-h-touch w-full rounded-xl bg-accent px-4 font-medium text-ink active:bg-accent-press"
      >
        {t('auth.sessionEnded.action')}
      </button>
    </main>
  );
}

function RequireAccount({ children }: { children: ReactNode }): ReactNode {
  const { state } = useAccount();
  if (state.status === 'loading') return <LoadingScreen />;
  if (state.status === 'onboarding-required') return <Navigate to="/onboarding" replace />;
  if (state.status === 'signed-out') return <SessionEndedScreen />;
  return children;
}

// Incoming-call phases that mean "accepted and should be on the call screen".
// (dialing/ringing_remote are outgoing — useDialer already routes those.)
const LIVE_INCOMING_PHASES = new Set(['connecting', 'in_call', 'reconnecting']);

/**
 * Pure decision for the desktop "land on /call when an accepted incoming call
 * goes live" rule. Returns whether to navigate and the callId to remember as
 * routed. Fires once per callId (so leaving /call mid-call is never
 * overridden), never while still ringing, and never when already on /call.
 * Exported for unit testing the exact accept-from-push regression.
 */
export function desktopCallRoute(
  call: { callId: string; direction: string; phase: string } | null,
  pathname: string,
  routedCallId: string | null,
): { navigate: boolean; routedCallId: string | null } {
  if (!call) return { navigate: false, routedCallId: null };
  const live = call.direction === 'incoming' && LIVE_INCOMING_PHASES.has(call.phase);
  if (live && routedCallId !== call.callId && pathname !== '/call') {
    return { navigate: true, routedCallId: call.callId };
  }
  return { navigate: false, routedCallId };
}

/**
 * The desktop ring overlay (JW6 modal contract): mounted with the shell so a
 * ring reaches the user on every desktop route with no navigation.
 *
 * It also lands the pane on /call the moment an INCOMING call goes live, which
 * is the only way the in-call surface appears on desktop after an accept. This
 * must not depend on having observed the brief `ringing` render first: a
 * notification "Accept" auto-answers on cold open (installAutoAccept) fast
 * enough to skip it, which is exactly the "accepted but stuck on the contact
 * list, no way to hang up" bug. So we navigate once per callId whenever the
 * call reaches a live incoming phase — covering both the in-modal Accept tap
 * and the push auto-accept. Keyed by callId so leaving /call mid-call (the
 * return-to-call bar) is never overridden, and a later call still routes.
 */
function DesktopRingModal(): ReactNode {
  const { engine } = useScreens();
  const engineState = useStore(engine.state);
  const navigate = useNavigate();
  const location = useLocation();
  const call = engineState.call;
  const routedRef = useRef<string | null>(null);
  useEffect(() => {
    const decision = desktopCallRoute(call, location.pathname, routedRef.current);
    routedRef.current = decision.routedCallId;
    if (decision.navigate) void navigate('/call');
  }, [call, navigate, location.pathname]);
  return <IncomingCallScreen variant="modal" />;
}

/** Routes whose content lives in the desktop sidebar — their pane is empty. */
const SIDEBAR_ROUTES = new Set(['/', '/contacts', '/incoming']);

function ScreenRouteElement({ route }: { route: (typeof SCREEN_ROUTES)[number] }): ReactNode {
  const isDesktop = useIsDesktop();
  const { path, Component } = route;
  const tab = 'tab' in route && route.tab;
  if (!isDesktop) {
    // Compact: exactly the pre-redesign split — tab bar for the three primary
    // screens (AppShell delegates to TabLayout below the breakpoint), bare
    // full-screen for everything else.
    return tab ? (
      <AppShell>
        <Component />
      </AppShell>
    ) : (
      <Component />
    );
  }
  // /incoming stays a valid deep-link target on desktop: the shell mounts and
  // the modal (driven by engine/pickup state, not the route) does the ringing.
  const pane = SIDEBAR_ROUTES.has(path) ? <EmptyPane /> : <Component />;
  return (
    <AppShell sidebar={<Sidebar />}>
      {pane}
      <DesktopRingModal />
    </AppShell>
  );
}

function OnboardingRoute(): ReactNode {
  const { state, completeOnboarding, restoreWithPasskey, primePasskeyRestore, onboardingFlowActive, setOnboardingFlowActive } =
    useAccount();
  const navigate = useNavigate();
  // Auth completes BEFORE the flow's permission-priming steps, flipping the
  // account state to 'ready' — onboardingFlowActive keeps the flow mounted
  // through those steps instead of auto-redirecting. This has to live in
  // AccountContext, not local state here: AppRoot (ReadyApp.tsx) swaps <App/>
  // for a freshly-keyed <ReadyApp/> the instant state.status becomes 'ready',
  // which would destroy any flag kept in this component before it could do
  // anything. A killed app relaunches straight into the main app (auth
  // persisted, permissions re-arm at call time).
  if (state.status === 'loading') return <LoadingScreen />;
  if (state.status === 'ready' && !onboardingFlowActive) return <Navigate to="/" replace />;
  return (
    <OnboardingFlow
      onComplete={async (result) => {
        setOnboardingFlowActive(true);
        await completeOnboarding(result);
      }}
      onFinished={() => {
        void navigate('/', { replace: true });
        setOnboardingFlowActive(false);
      }}
      onPasskeyRestore={restoreWithPasskey}
      onPrimePasskeyRestore={primePasskeyRestore}
    />
  );
}

export function App(): ReactNode {
  return (
    <Routes>
      <Route path="/onboarding" element={<OnboardingRoute />} />
      {SCREEN_ROUTES.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={
            <RequireAccount>
              <ScreenRouteElement route={route} />
            </RequireAccount>
          }
        />
      ))}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
